import React, { useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  LinearProgress,
  Checkbox,
  Avatar,
  IconButton,
  TextField,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Divider,
} from "@mui/material";

import { FaFileExcel, FaUpload, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import useApiEp from "../../hooks/useApiEp";

function ContactsTabs({
  campaignData,
  setCampaignData,
  tabState,
  setTabState,
  isDragged,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleFileUpload,
  downloadTemplate,
  uploadProgress,
  selectedCount,
  indeterminate,
  allSelected,
  handleSelectAll,
  searchQuery,
  setSearchQuery,
  filteredContacts,
  toggleSelect,
  removeContact,
  contactGroupName,
  campaignGroupName,
}) {
  const set = (patch) => {
    setTabState((prev) => ({
      ...prev,
      ...patch,
    }));
  };

  const {
    tabValue,
    selectedList,
    dbColumns,
    dbStartIndex,
    dbEndIndex,
    pendingGroup,
    groupSearchQuery,
    groupStartIndex,
    groupEndIndex,
  } = tabState;

  const tabLabels = [
    "Import from Excel",
    "Use Database",
    "Quick List",
    "Contacts List",
  ];

  // =========================================================================
  // CAMPAIGN GROUP LOCK
  // =========================================================================

  const isCampaignGroupLocked =
    typeof campaignGroupName === "string" &&
    campaignGroupName.trim() !== "" &&
    (tabValue === 1 || tabValue === 3);

  // =========================================================================
  // TAB 1 API
  //
  // ONLY enabled when tabValue === 1
  // =========================================================================

  const {
    data: dbLists,
    isLoading: loadingDbLists,
    isError: isErrorDbLists,
  } = useApiEp("/user/get-my-assigned-dbs", tabValue === 1);

  // =========================================================================
  // TAB 3 API
  //
  // ONLY enabled when tabValue === 3
  //
  // groupList = string[]
  // =========================================================================

  const {
    data: groupList,
    isLoading: loadingGroupList,
    isError: isErrorGroupList,
  } = useApiEp("/user/group-list", tabValue === 3);

  // =========================================================================
  // NORMAL TAB 1 COUNT
  // =========================================================================

  const {
    data: filterCount,
    isLoading: loadingFilterCount,
    isError: isErrorFilterCount,
    error: errorFilterCount,
  } = useApiEp(
    selectedList
      ? `/database/get-list-count?listId=${selectedList}${
          dbStartIndex !== "" ? `&startIndex=${dbStartIndex}` : ""
        }${dbEndIndex !== "" ? `&endIndex=${dbEndIndex}` : ""}`
      : "",
    tabValue === 1 && !isCampaignGroupLocked && !!selectedList
  );

  // =========================================================================
  // LOCKED TAB 1
  //
  // contactGroupName should correspond to one of assigned DBs.
  // =========================================================================

  const lockedDbList =
    tabValue === 1 && Array.isArray(dbLists)
      ? dbLists.find((item) => item?.name === contactGroupName)
      : undefined;

  /*
   * Your existing API was using the DB name here.
   * If this endpoint actually requires _id,
   * change lockedDbListName to lockedDbList?._id.
   */
  const lockedDbListName = lockedDbList?.name;

  // =========================================================================
  // LOCKED TAB 3
  //
  // group-list is string[].
  // =========================================================================

  const lockedContactGroupExists =
    tabValue === 3 &&
    Array.isArray(groupList) &&
    groupList.includes(contactGroupName);

  // =========================================================================
  // DATABASE PREVIEW
  //
  // Locked tab 1 only.
  //
  // IMPORTANT:
  // no startIndex/endIndex in locked mode.
  // =========================================================================

  const {
    data: dbContactsPreview,
    isLoading: loadingDbContactsPreview,
    isError: isErrorDbContactsPreview,
    error: errorDbContactsPreview,
  } = useApiEp(
    tabValue === 1 && isCampaignGroupLocked && lockedDbListName
      ? `/user/get-db-contacts-preview/${encodeURIComponent(lockedDbListName)}`
      : "",
    tabValue === 1 && isCampaignGroupLocked && !!lockedDbListName
  );

  // =========================================================================
  // CONTACT GROUP PREVIEW
  //
  // Normal tab 3:
  // pendingGroup
  //
  // Locked tab 3:
  // contactGroupName
  // =========================================================================

  const groupToPreview =
    tabValue === 3
      ? isCampaignGroupLocked
        ? contactGroupName
        : pendingGroup
      : "";

  const {
    data: contactsGroupPreview,
    isLoading: loadingContactsGroupPreview,
    isError: isErrorContactsGroupPreview,
    error: errorContactsGroupPreview,
  } = useApiEp(
    tabValue === 3 && groupToPreview
      ? `/user/get-contacts-preview-group/${encodeURIComponent(groupToPreview)}`
      : "",
    tabValue === 3 && !!groupToPreview
  );

  // =========================================================================
  // ACTIVE PREVIEW
  // =========================================================================

  const groupPreview =
    tabValue === 1 ? dbContactsPreview : contactsGroupPreview;

  const loadingGroupPreview =
    tabValue === 1 ? loadingDbContactsPreview : loadingContactsGroupPreview;

  const isErrorGroupPreview =
    tabValue === 1 ? isErrorDbContactsPreview : isErrorContactsGroupPreview;

  const errorGroupPreview =
    tabValue === 1 ? errorDbContactsPreview : errorContactsGroupPreview;

  // =========================================================================
  // EFFECTIVE GROUP
  // =========================================================================

  const effectivePendingGroup = isCampaignGroupLocked
    ? contactGroupName
    : pendingGroup;

  // =========================================================================
  // DUPLICATE CHECK
  // =========================================================================

  const checkForDuplicates = (newContacts) => {
    const existingPhones = new Set(
      (campaignData.contacts || []).map((contact) => contact.phone)
    );

    return newContacts.filter((contact) => {
      if (existingPhones.has(contact.phone)) {
        toast.warn(`Duplicate phone number ${contact.phone} skipped`);

        return false;
      }

      return true;
    });
  };

  // =========================================================================
  // GROUP SEARCH
  //
  // groupList is string[]
  // =========================================================================

  const filteredGroupList = Array.isArray(groupList)
    ? groupList.filter((group) =>
        String(group)
          .toLowerCase()
          .includes((groupSearchQuery || "").toLowerCase())
      )
    : [];

  // =========================================================================
  // TAB 1 HANDLERS
  // =========================================================================

  const handleChangeList = (event) => {
    const listId = event.target.value;

    const list = dbLists?.find((item) => item._id === listId);

    set({
      selectedList: listId,
      dbColumns: list?.columns || [],
    });

    setCampaignData((prev) => ({
      ...prev,
      selectedList: listId,
    }));
  };

  const handleConfirmDbSelection = () => {
    if (!selectedList) {
      toast.error("Please select a list first");

      return;
    }

    setCampaignData((prev) => ({
      ...prev,

      contacts: [],

      selectedGroup: selectedList,

      startIndex: dbStartIndex !== "" ? dbStartIndex : undefined,

      endIndex: dbEndIndex !== "" ? dbEndIndex : undefined,

      dbGroup: "database",
    }));

    toast.success("Database list selected for campaign");

    set({
      selectedList: "",
      dbColumns: [],
      dbStartIndex: "",
      dbEndIndex: "",
    });
  };

  // =========================================================================
  // TAB 2 QUICK LIST
  // =========================================================================

  const [quickListText, setQuickListText] = useState("");

  const handleAddQuickList = (value, clear) => {
    if (!value.trim()) {
      toast.error("Please enter phone numbers");

      return;
    }

    const phoneNumbers = value
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    if (phoneNumbers.length === 0) {
      toast.error("No valid phone numbers found");

      return;
    }

    const newContacts = checkForDuplicates(
      phoneNumbers.map((phone, index) => ({
        id: Date.now() + index,

        name: "",

        phone,

        selected: true,
      }))
    );

    if (newContacts.length === 0) {
      toast.error("No new contacts added (all were duplicates)");

      return;
    }

    setCampaignData((prev) => ({
      ...prev,

      contacts: [...(prev.contacts || []), ...newContacts],

      selectedGroup: "",
    }));

    clear("");

    toast.success(`Added ${newContacts.length} contacts from quick list`);
  };

  // =========================================================================
  // TAB 3 HANDLERS
  // =========================================================================

  const handleSelectPendingGroup = (groupName) => {
    set({
      pendingGroup: groupName,

      groupSearchQuery: "",

      groupStartIndex: "",

      groupEndIndex: "",
    });
  };

  const handleClearPendingGroup = () => {
    set({
      pendingGroup: "",

      groupSearchQuery: "",

      groupStartIndex: "",

      groupEndIndex: "",
    });
  };

  // =========================================================================
  // CONFIRM LOCKED / NORMAL GROUP
  // =========================================================================

  const handleConfirmGroupSelection = () => {
    const targetGroup = isCampaignGroupLocked ? contactGroupName : pendingGroup;

    if (!targetGroup) {
      toast.error("Please select a group first");

      return;
    }

    let targetId = targetGroup;

    /*
     * Locked database
     */
    if (isCampaignGroupLocked && tabValue === 1) {
      if (!lockedDbListName) {
        toast.error("Assigned database not found");

        return;
      }

      targetId = lockedDbListName;
    }

    /*
     * Locked contacts group
     */
    if (isCampaignGroupLocked && tabValue === 3) {
      if (!lockedContactGroupExists) {
        toast.error(`Contact group "${contactGroupName}" is not available`);

        return;
      }

      targetId = contactGroupName;
    }

    setCampaignData((prev) => {
      const next = {
        ...prev,

        contacts: [],

        selectedGroup: targetId,

        dbGroup: tabValue === 1 ? "database" : "contacts",
      };

      /*
       * LOCKED
       *
       * Range does not apply.
       *
       * Explicitly remove stale values.
       */
      if (isCampaignGroupLocked) {
        delete next.startIndex;
        delete next.endIndex;
      } else {

      /*
       * NORMAL CONTACT GROUP
       *
       * Range remains supported.
       */
        next.startIndex = groupStartIndex !== "" ? groupStartIndex : undefined;

        next.endIndex = groupEndIndex !== "" ? groupEndIndex : undefined;
      }

      return next;
    });

    toast.success(`"${targetGroup}" selected for campaign`);

    if (!isCampaignGroupLocked) {
      handleClearPendingGroup();
    }
  };

  // =========================================================================
  // PREVIEW CONTACTS
  // =========================================================================

  const renderPreviewContacts = () => {
    if (!groupPreview) {
      return null;
    }

    const totalCount = groupPreview.totalCount ?? groupPreview.count ?? 0;

    const contacts = Array.isArray(groupPreview.contacts)
      ? groupPreview.contacts
      : [];

    return (
      <>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Total contacts: <strong>{totalCount}</strong>
        </Typography>

        {/* =============================================================== */}
        {/* RANGE ONLY FOR NORMAL MODE */}
        {/* =============================================================== */}

        {!isCampaignGroupLocked && tabValue === 3 && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
            }}
          >
            <TextField
              label="Start Index"
              type="number"
              size="small"
              value={groupStartIndex}
              onChange={(e) =>
                set({
                  groupStartIndex: e.target.value,
                })
              }
              sx={{ flex: 1 }}
              inputProps={{
                min: 0,

                max: Math.max(totalCount - 1, 0),
              }}
              helperText="Leave blank for beginning"
            />

            <TextField
              label="End Index"
              type="number"
              size="small"
              value={groupEndIndex}
              onChange={(e) =>
                set({
                  groupEndIndex: e.target.value,
                })
              }
              sx={{ flex: 1 }}
              inputProps={{
                min: groupStartIndex || 0,

                max: Math.max(totalCount - 1, 0),
              }}
              helperText="Leave blank for all contacts"
            />
          </Box>
        )}

        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          Sample preview (up to 2 contacts):
        </Typography>

        <Paper
          variant="outlined"
          sx={{
            p: 1,
            mb: 2,
          }}
        >
          {contacts.length === 0 ? (
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                p: 1,
                textAlign: "center",
              }}
            >
              No preview contacts available.
            </Typography>
          ) : (
            contacts.map((contact, index) => (
              <React.Fragment key={index}>
                {index > 0 && (
                  <Divider
                    sx={{
                      my: 0.5,
                    }}
                  />
                )}

                <Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  sx={{
                    p: 1,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: "primary.main",
                    }}
                  >
                    {typeof contact?.name === "string" && contact.name.trim()
                      ? contact.name.charAt(0).toUpperCase()
                      : "?"}
                  </Avatar>

                  <Box>
                    <Typography variant="body1">
                      {contact?.name || "Unknown"}
                    </Typography>

                    <Typography variant="body2" color="textSecondary">
                      {contact?.phoneNumber || contact?.phone || "-"}
                    </Typography>
                  </Box>
                </Box>
              </React.Fragment>
            ))
          )}
        </Paper>

        <Button
          variant="contained"
          fullWidth
          disabled={totalCount === 0}
          onClick={handleConfirmGroupSelection}
        >
          Confirm — Use "{effectivePendingGroup}"{" "}
          {isCampaignGroupLocked
            ? `(${totalCount} contacts)`
            : groupStartIndex !== "" || groupEndIndex !== ""
            ? `(Indices ${groupStartIndex || "start"} – ${
                groupEndIndex || "end"
              })`
            : `(${totalCount} contacts)`}
        </Button>
      </>
    );
  };

  // =========================================================================
  // RENDER
  // =========================================================================

  return (
    <Box sx={{ width: "100%" }}>
      {/* ================================================================== */}
      {/* TAB BAR */}
      {/* ================================================================== */}

      {isCampaignGroupLocked ? (
        /*
         * LOCKED
         *
         * Exactly ONE tab.
         *
         * IMPORTANT:
         * Tab retains actual tabValue.
         *
         * If tabValue = 1:
         * value = 1
         *
         * If tabValue = 3:
         * value = 3
         */
        <Tabs value={tabValue}>
          <Tab value={tabValue} label={tabLabels[tabValue] || "Contacts"} />
        </Tabs>
      ) : (
        /*
         * NORMAL
         *
         * All four tabs.
         */
        <Tabs
          value={tabValue}
          onChange={(event, value) => {
            set({
              tabValue: value,
            });

            handleClearPendingGroup();
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Import from Excel" />

          <Tab label="Use Database" />

          <Tab label="Quick List" />

          <Tab label="Contacts List" />
        </Tabs>
      )}

      {/* ================================================================== */}
      {/* TAB 0 — EXCEL */}
      {/* ================================================================== */}

      {!isCampaignGroupLocked && tabValue === 0 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom color="textSecondary">
            Upload your contacts via Excel file
          </Typography>

          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById("file-upload")?.click()}
            sx={{
              border: "2px dashed #ccc",

              p: 4,

              textAlign: "center",

              cursor: "pointer",

              borderRadius: 2,

              bgcolor: isDragged ? "grey.100" : "background.paper",
            }}
          >
            <input
              id="file-upload"
              type="file"
              accept=".xlsx,.xls"
              style={{
                display: "none",
              }}
              onChange={(e) => {
                const file = e.target.files?.[0];

                if (file) {
                  handleFileUpload(file, checkForDuplicates);
                }
              }}
            />

            <FaFileExcel
              size={48}
              color="#217346"
              style={{
                marginBottom: 16,
              }}
            />

            <Typography variant="h6" gutterBottom>
              {campaignData.uploadedFile
                ? "File Uploaded!"
                : "Drop your Excel file here"}
            </Typography>

            <Typography variant="body2" color="textSecondary">
              Supports .xlsx and .xls files with phone numbers
            </Typography>

            <Button variant="contained" startIcon={<FaUpload />} sx={{ mt: 2 }}>
              Browse Files
            </Button>

            <Button
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();

                downloadTemplate();
              }}
              sx={{
                mt: 2,
                ml: 2,
              }}
            >
              Download Template
            </Button>
          </Box>

          {uploadProgress > 0 && (
            <Box sx={{ mt: 3 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />

              <Typography variant="body2" sx={{ mt: 1 }}>
                {uploadProgress === 100 ? "Uploaded" : "Uploading..."}{" "}
                {uploadProgress}%
              </Typography>
            </Box>
          )}

          {campaignData.contacts?.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="h6">
                  Uploaded Contacts ({selectedCount} selected /{" "}
                  {campaignData.contacts.length} total)
                </Typography>

                <Box display="flex" alignItems="center">
                  <Checkbox
                    indeterminate={indeterminate}
                    checked={allSelected}
                    onChange={handleSelectAll}
                  />

                  <Typography>Select All</Typography>
                </Box>
              </Box>

              <TextField
                fullWidth
                placeholder="Search by phone number"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ mb: 2 }}
              />

              <Paper
                variant="outlined"
                sx={{
                  p: 2,

                  maxHeight: 400,

                  overflow: "auto",
                }}
              >
                {filteredContacts.map((contact) => (
                  <Box
                    key={contact.id}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      mb: 1,

                      p: 1,

                      bgcolor: "grey.50",

                      borderRadius: 1,
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Checkbox
                        checked={contact.selected}
                        onChange={() => toggleSelect(contact.id)}
                      />

                      <Avatar
                        sx={{
                          width: 32,

                          height: 32,

                          bgcolor: "primary.main",
                        }}
                      >
                        {contact.name ? contact.name.charAt(0) : "?"}
                      </Avatar>

                      <Box>
                        <Typography variant="body1">
                          {contact.name || "Unknown"}
                        </Typography>

                        <Typography variant="body2" color="textSecondary">
                          {contact.phone}
                        </Typography>
                      </Box>
                    </Box>

                    <IconButton
                      size="small"
                      onClick={() => removeContact(contact.id)}
                    >
                      <FaTrash color="#f44336" />
                    </IconButton>
                  </Box>
                ))}
              </Paper>
            </Box>
          )}
        </Box>
      )}

      {/* ================================================================== */}
      {/* TAB 1 — DATABASE NORMAL */}
      {/* ================================================================== */}

      {!isCampaignGroupLocked && tabValue === 1 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Select a Saved List from Database
          </Typography>

          {loadingDbLists && (
            <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
              <CircularProgress size={18} />

              <Typography variant="body2">Loading databases...</Typography>
            </Box>
          )}

          {isErrorDbLists && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              Failed to load assigned databases.
            </Typography>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="list-select-label">Contact List</InputLabel>

            <Select
              labelId="list-select-label"
              value={selectedList}
              label="Contact List"
              onChange={handleChangeList}
            >
              {dbLists?.map((list) => (
                <MenuItem key={list._id} value={list._id}>
                  {list.name} ({list.columns?.length || 0} columns)
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* RANGE IS NORMAL TAB 1 ONLY */}

          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
            }}
          >
            <TextField
              label="Start Index"
              type="number"
              size="small"
              value={dbStartIndex}
              onChange={(e) =>
                set({
                  dbStartIndex: e.target.value,
                })
              }
              sx={{ flex: 1 }}
              inputProps={{
                min: 0,
              }}
            />

            <TextField
              label="End Index"
              type="number"
              size="small"
              value={dbEndIndex}
              onChange={(e) =>
                set({
                  dbEndIndex: e.target.value,
                })
              }
              sx={{ flex: 1 }}
              inputProps={{
                min: dbStartIndex || 0,
              }}
            />
          </Box>

          {loadingFilterCount && (
            <Typography variant="body2" color="textSecondary">
              Loading contact count...
            </Typography>
          )}

          {isErrorFilterCount && (
            <Typography variant="body2" color="error">
              Error fetching contact count: {errorFilterCount?.message}
            </Typography>
          )}

          {filterCount && (
            <Typography variant="subtitle1" gutterBottom>
              Total Contacts in List: {filterCount.count}
            </Typography>
          )}

          {dbColumns?.length > 0 && (
            <>
              <Typography variant="subtitle1" gutterBottom>
                Column Preview:
              </Typography>

              <Paper
                variant="outlined"
                sx={{
                  p: 2,

                  mb: 2,

                  maxHeight: 300,

                  overflow: "auto",
                }}
              >
                {dbColumns.map((column, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 1,

                      p: 1,

                      bgcolor: "grey.50",

                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body1">{column}</Typography>
                  </Box>
                ))}
              </Paper>

              <Button
                variant="contained"
                fullWidth
                onClick={handleConfirmDbSelection}
              >
                Confirm Selection
              </Button>
            </>
          )}
        </Box>
      )}

      {/* ================================================================== */}
      {/* TAB 2 — QUICK LIST */}
      {/* ================================================================== */}

      {!isCampaignGroupLocked && tabValue === 2 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Add Quick List (Phone Numbers only)
          </Typography>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Paste phone numbers below, one per line.
          </Typography>

          <TextField
            multiline
            rows={8}
            fullWidth
            placeholder={"eg. 9876543210\n9123456789\n9988776655"}
            value={quickListText}
            onChange={(e) => setQuickListText(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Button
            variant="contained"
            fullWidth
            onClick={() => handleAddQuickList(quickListText, setQuickListText)}
          >
            Add Quick List
          </Button>

          {campaignData.contacts?.length > 0 && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mt: 2,

                maxHeight: 300,

                overflow: "auto",
              }}
            >
              {campaignData.contacts.map((contact) => (
                <Box
                  key={contact.id}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    mb: 1,

                    p: 1,

                    bgcolor: "grey.50",

                    borderRadius: 1,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar
                      sx={{
                        width: 32,

                        height: 32,

                        bgcolor: "primary.main",
                      }}
                    >
                      {contact.name ? contact.name.charAt(0) : "?"}
                    </Avatar>

                    <Box>
                      <Typography>{contact.name || "Unknown"}</Typography>

                      <Typography variant="body2" color="textSecondary">
                        {contact.phone}
                      </Typography>
                    </Box>
                  </Box>

                  <IconButton
                    size="small"
                    onClick={() => removeContact(contact.id)}
                  >
                    <FaTrash color="#f44336" />
                  </IconButton>
                </Box>
              ))}
            </Paper>
          )}
        </Box>
      )}

      {/* ================================================================== */}
      {/* LOCKED TAB 1 — DATABASE */}
      {/* ================================================================== */}

      {isCampaignGroupLocked && tabValue === 1 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Assigned Database
          </Typography>

          {loadingDbLists && (
            <Box display="flex" gap={1} alignItems="center">
              <CircularProgress size={18} />

              <Typography variant="body2">Loading database...</Typography>
            </Box>
          )}

          {!loadingDbLists && !lockedDbList && (
            <Typography color="error" variant="body2">
              Assigned database "{contactGroupName}" was not found.
            </Typography>
          )}

          {lockedDbList && (
            <>
              <Chip label={lockedDbList.name} color="primary" sx={{ mb: 2 }} />

              {loadingGroupPreview && (
                <Box display="flex" gap={1} alignItems="center" sx={{ mb: 2 }}>
                  <CircularProgress size={18} />

                  <Typography variant="body2">Loading preview...</Typography>
                </Box>
              )}

              {isErrorGroupPreview && (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                  {errorGroupPreview?.message ||
                    "Failed to load database preview."}
                </Typography>
              )}

              {/* NO RANGE HERE */}

              {renderPreviewContacts()}
            </>
          )}
        </Box>
      )}

      {/* ================================================================== */}
      {/* TAB 3 — CONTACT GROUP */}
      {/* ================================================================== */}

      {tabValue === 3 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            {isCampaignGroupLocked ? "Contact Group" : "Select a Contact Group"}
          </Typography>

          {/* =============================================================== */}
          {/* LOCKED TAB 3 */}
          {/* =============================================================== */}

          {isCampaignGroupLocked ? (
            <>
              {loadingGroupList && (
                <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                  <CircularProgress size={18} />

                  <Typography variant="body2">
                    Loading contact group...
                  </Typography>
                </Box>
              )}

              {isErrorGroupList && (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                  Failed to load contact groups.
                </Typography>
              )}

              {!loadingGroupList &&
                Array.isArray(groupList) &&
                !lockedContactGroupExists && (
                  <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                    Contact group "{contactGroupName}" was not found.
                  </Typography>
                )}

              {lockedContactGroupExists && (
                <>
                  <Chip
                    label={contactGroupName}
                    color="primary"
                    sx={{ mb: 2 }}
                  />

                  {loadingGroupPreview && (
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{ mb: 2 }}
                    >
                      <CircularProgress size={18} />

                      <Typography variant="body2">
                        Loading preview...
                      </Typography>
                    </Box>
                  )}

                  {isErrorGroupPreview && (
                    <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                      {errorGroupPreview?.message || "Failed to load preview."}
                    </Typography>
                  )}

                  {/* NO RANGE HERE */}

                  {renderPreviewContacts()}
                </>
              )}
            </>
          ) : (
            /*
             * NORMAL TAB 3
             */
            <>
              {!pendingGroup && (
                <>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search groups..."
                    value={groupSearchQuery}
                    onChange={(e) =>
                      set({
                        groupSearchQuery: e.target.value,
                      })
                    }
                    sx={{ mb: 2 }}
                  />

                  {loadingGroupList && (
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{ mb: 2 }}
                    >
                      <CircularProgress size={18} />

                      <Typography variant="body2">Loading groups...</Typography>
                    </Box>
                  )}

                  {isErrorGroupList && (
                    <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                      Failed to load groups.
                    </Typography>
                  )}

                  {!loadingGroupList && filteredGroupList.length === 0 && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mb: 2 }}
                    >
                      No groups found.
                    </Typography>
                  )}

                  <Paper
                    variant="outlined"
                    sx={{
                      maxHeight: 320,

                      overflow: "auto",
                    }}
                  >
                    <List disablePadding>
                      {filteredGroupList.map((group) => (
                        <ListItem key={group} disablePadding divider>
                          <ListItemButton
                            onClick={() => handleSelectPendingGroup(group)}
                          >
                            <Avatar
                              sx={{
                                width: 32,

                                height: 32,

                                bgcolor: "secondary.main",

                                mr: 2,

                                fontSize: 14,
                              }}
                            >
                              {String(group).charAt(0).toUpperCase()}
                            </Avatar>

                            <ListItemText primary={group} />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </>
              )}

              {pendingGroup && (
                <>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{ mb: 2 }}
                  >
                    <Chip
                      label={pendingGroup}
                      onDelete={handleClearPendingGroup}
                      color="primary"
                    />

                    <Typography variant="body2" color="textSecondary">
                      — click × to pick a different group
                    </Typography>
                  </Box>

                  {loadingGroupPreview && (
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{ mb: 2 }}
                    >
                      <CircularProgress size={18} />

                      <Typography variant="body2">
                        Loading preview...
                      </Typography>
                    </Box>
                  )}

                  {isErrorGroupPreview && (
                    <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                      {errorGroupPreview?.message || "Failed to load preview."}
                    </Typography>
                  )}

                  {renderPreviewContacts()}
                </>
              )}
            </>
          )}

          {/* =============================================================== */}
          {/* CONFIRM SUMMARY */}
          {/* =============================================================== */}

          {campaignData.selectedGroup &&
            campaignData.dbGroup ===
              (tabValue === 1 ? "database" : "contacts") && (
              <Box
                sx={{
                  mt: 3,

                  p: 2,

                  bgcolor: "success.50",

                  border: "1px solid",

                  borderColor: "success.light",

                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle1" color="success.dark">
                  ✓ <strong>{effectivePendingGroup}</strong> confirmed
                  {isCampaignGroupLocked
                    ? " · All contacts"
                    : campaignData.startIndex !== undefined ||
                      campaignData.endIndex !== undefined
                    ? ` · Indices ${campaignData.startIndex ?? "start"} – ${
                        campaignData.endIndex ?? "end"
                      }`
                    : " · All contacts"}
                </Typography>
              </Box>
            )}
        </Box>
      )}
    </Box>
  );
}

export default ContactsTabs;
